import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { ArrowLeft } from "lucide-react"
import { useCallback } from "react"
import {
	CouponProgramForm,
	type CouponProgramFormValues,
} from "@/components/coupons/coupon-program-form"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import { useCouponProgram, useUpdateCouponProgram } from "@/queries/marketplace"

export const Route = createFileRoute("/coupons/programs/$programId/edit")({
	component: EditCouponProgramPage,
})

function EditCouponProgramPage() {
	const { programId } = Route.useParams()
	const navigate = useNavigate()
	const { program, isLoading, error } = useCouponProgram({ programId })
	const updateMutation = useUpdateCouponProgram()

	const handleSubmit = useCallback(
		async (values: CouponProgramFormValues) => {
			await updateMutation.mutateAsync({
				id: programId,
				name: values.name,
				codePrefix: values.codePrefix || null,
				description: values.description,
				type: values.type,
				value: values.value,
				currencyAmounts: values.currencyAmounts,
				region: values.region,
				minimumOrderAmount: values.minimumOrderAmount,
				maximumDiscountAmount: values.maximumDiscountAmount,
				validityDays: values.validityDays,
				newBuyersOnly: values.newBuyersOnly,
				combinable: values.combinable,
			})
			navigate({
				to: "/coupons/programs/$programId",
				params: { programId },
			})
		},
		[programId, updateMutation, navigate],
	)

	if (isLoading) {
		return (
			<div className="flex flex-col gap-6 p-6">
				<Skeleton className="h-8 w-64" />
				<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
					<Skeleton className="h-64" />
					<Skeleton className="h-64" />
				</div>
			</div>
		)
	}

	if (error || !program) {
		return (
			<div className="flex flex-col items-start gap-3 p-6">
				<p className="text-bad">
					{error?.message || "The coupon program could not be found."}
				</p>
				<Button variant="outline" asChild>
					<Link to="/coupons/programs">
						<ArrowLeft />
						Back to programs
					</Link>
				</Button>
			</div>
		)
	}

	return (
		<div className="p-6">
			<CouponProgramForm
				mode="edit"
				program={program}
				onSubmit={handleSubmit}
				isSubmitting={updateMutation.isPending}
			/>
		</div>
	)
}
